import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import DropDownMultiple, {
  type DropDownOptionBase,
} from "../../components/staff/DropDownMultiple";
import Button from "../../components/staff/Button";
import {
  getAllAccountingFees,
  saveFiscalYearFees,
} from "../../Services/AccountingFees";
import { getCurrentFiscalYear } from "../../utils/dates";
import { billingPeriodRangeLabel } from "../../utils/billingYear";
import {
  appendDraftFiscalYearOption,
  applyIncreaseToRows,
  buildCreateFiscalYearFeesPayload,
  buildFiscalYearOptions,
  buildSaveFiscalYearFeesPayload,
  indexAccountingFeesByYear,
  isDraftFiscalYear,
  mapFeesToFormState,
  mapNewFiscalYearFormState,
  resolveDefaultFiscalYear,
  resolveNextFiscalYear,
  type AccountingFeesYearIndex,
  type ManageFiscalYearFormState,
  type TariffRangeRow,
} from "../../utils/manageFiscalYearFees";
import {
  AccountingModalCheckbox,
  AccountingModalError,
  AccountingModalFooter,
  AccountingModalTabs,
  formatMoney,
} from "./accountingModalUi";
import {
  ACCOUNTING_INSET_PANEL_CLASS,
  ACCOUNTING_PANEL_CLASS,
  SECTION_TITLE_CLASS,
} from "./accountingFirmPanels";

type ManageFiscalYearModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type TariffTab = "initiation" | "legal" | "accounting" | "investment";

const TARIFF_TABS: { key: TariffTab; label: string }[] = [
  { key: "initiation", label: "Initiation Fee" },
  { key: "legal", label: "Legal" },
  { key: "accounting", label: "Accounting" },
  { key: "investment", label: "Investment Banking" },
];

function TariffTable({
  rows,
  showRangeColumn = true,
  currentFeeLabel = "Current Fee (Calculated)",
}: {
  rows: TariffRangeRow[];
  showRangeColumn?: boolean;
  currentFeeLabel?: string;
}): React.ReactElement {
  if (rows.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-gray-font">
        No fee data available for this fiscal year.
      </p>
    );
  }

  return (
    <div className="table-responsive tabledata w-full overflow-x-auto">
      <table className="table min-w-full w-max">
        <thead>
          <tr className="bg-accent-Default-dark text-white">
            {showRangeColumn && (
              <th className="px-4 py-2 text-left text-xs font-medium">
                Professionals Range
              </th>
            )}
            <th className="px-4 py-2 text-left text-xs font-medium">
              {currentFeeLabel}
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium">
              Previous Year Base
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.range}
              className="even:bg-gray-100 text-gray-font text-xs"
            >
              {showRangeColumn && (
                <td className="px-4 py-2">{row.range}</td>
              )}
              <td className="px-4 py-2 font-semibold text-primary">
                {formatMoney(row.currentFee)}
              </td>
              <td className="px-4 py-2 text-gray-400">
                {row.previousBase !== null
                  ? formatMoney(row.previousBase)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IncreaseField({
  prevYearRef,
  value,
  onChange,
  disabled = false,
}: {
  prevYearRef: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <div className="w-full max-w-xs">
      <Input
        title="Increase (%)"
        type="number"
        value={value}
        placeholder="0"
        percentage
        error={false}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {prevYearRef ? (
        <p className="mt-1 text-xs text-gray-font">{prevYearRef}</p>
      ) : null}
    </div>
  );
}

export default function ManageFiscalYearModal({
  isOpen,
  onClose,
}: ManageFiscalYearModalProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fiscalYearOptions, setFiscalYearOptions] = useState<DropDownOptionBase[]>(
    [],
  );
  const [feesByYear, setFeesByYear] = useState<AccountingFeesYearIndex>(
    new Map(),
  );
  const [selectedFiscalYear, setSelectedFiscalYear] =
    useState<DropDownOptionBase | null>(null);
  const [activeTariffTab, setActiveTariffTab] = useState<TariffTab>("initiation");
  const [syncAccountingWithLegal, setSyncAccountingWithLegal] = useState(true);
  const [formState, setFormState] = useState<ManageFiscalYearFormState | null>(
    null,
  );
  const [draftFiscalYears, setDraftFiscalYears] = useState<number[]>([]);
  const [draftFormByYear, setDraftFormByYear] = useState<
    Record<number, ManageFiscalYearFormState>
  >({});

  const applyFormState = useCallback(
    (year: number, index: AccountingFeesYearIndex) => {
      setFormState(mapFeesToFormState(year, index));
      setSyncAccountingWithLegal(true);
    },
    [],
  );

  const loadFiscalYearSelection = useCallback(
    (
      option: DropDownOptionBase,
      index: AccountingFeesYearIndex,
      drafts: number[],
      draftForms: Record<number, ManageFiscalYearFormState>,
    ) => {
      const year = Number(option.id);
      setSelectedFiscalYear(option);
      if (isDraftFiscalYear(year, index, drafts)) {
        setFormState(
          draftForms[year] ?? mapNewFiscalYearFormState(year, index),
        );
        setSyncAccountingWithLegal(true);
        return;
      }
      applyFormState(year, index);
    },
    [applyFormState],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setFiscalYearOptions([]);
    setFeesByYear(new Map());
    setSelectedFiscalYear(null);
    setFormState(null);
    setDraftFiscalYears([]);
    setDraftFormByYear({});
    setActiveTariffTab("initiation");

    void (async () => {
      try {
        const docs = await getAllAccountingFees();
        if (cancelled) {
          return;
        }

        const currentFiscalYear = getCurrentFiscalYear();
        const index = indexAccountingFeesByYear(docs);
        const options = buildFiscalYearOptions(docs, currentFiscalYear);

        setFeesByYear(index);
        setFiscalYearOptions(options);

        if (options.length === 0) {
          setError("No fiscal year fee records found.");
          return;
        }

        const defaultYear = resolveDefaultFiscalYear(options, currentFiscalYear);
        const defaultOption =
          options.find((option) => Number(option.id) === defaultYear) ??
          options[0];

        setSelectedFiscalYear(defaultOption);
        if (defaultOption) {
          applyFormState(Number(defaultOption.id), index);
        }
      } catch {
        if (!cancelled) {
          setError("Unable to load fiscal year fees.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, applyFormState]);

  const handleFiscalYearChange = (val: DropDownOptionBase | null) => {
    if (!val) {
      return;
    }

    const previousYear = selectedFiscalYear
      ? Number(selectedFiscalYear.id)
      : null;
    const nextDraftForms = { ...draftFormByYear };
    if (
      previousYear !== null &&
      formState &&
      draftFiscalYears.includes(previousYear)
    ) {
      nextDraftForms[previousYear] = formState;
      setDraftFormByYear(nextDraftForms);
    }

    loadFiscalYearSelection(val, feesByYear, draftFiscalYears, nextDraftForms);
  };

  const handleCreateNewFiscalYear = () => {
    setError("");
    const nextYear = resolveNextFiscalYear(feesByYear, draftFiscalYears);
    if (nextYear === null) {
      setError("Unable to create a new fiscal year without existing fee data.");
      return;
    }

    const nextOption: DropDownOptionBase = {
      id: String(nextYear),
      name: `${billingPeriodRangeLabel(nextYear - 1)} (New)`,
    };

    const previousYear = selectedFiscalYear
      ? Number(selectedFiscalYear.id)
      : null;
    const nextDraftForms = { ...draftFormByYear };
    if (
      previousYear !== null &&
      formState &&
      draftFiscalYears.includes(previousYear)
    ) {
      nextDraftForms[previousYear] = formState;
    }

    const updatedDraftYears = draftFiscalYears.includes(nextYear)
      ? draftFiscalYears
      : [...draftFiscalYears, nextYear];

    if (!draftFiscalYears.includes(nextYear)) {
      setDraftFiscalYears(updatedDraftYears);
      setFiscalYearOptions((current) =>
        appendDraftFiscalYearOption(current, nextYear),
      );
    }

    if (!nextDraftForms[nextYear]) {
      const newFormState = mapNewFiscalYearFormState(nextYear, feesByYear);
      if (!newFormState) {
        setError("Unable to prepare the new fiscal year from the latest fees.");
        return;
      }
      nextDraftForms[nextYear] = newFormState;
    }

    setDraftFormByYear(nextDraftForms);
    setSelectedFiscalYear(nextOption);
    setFormState(nextDraftForms[nextYear]);
    setSyncAccountingWithLegal(true);
  };

  const calculatedInitiationRows = useMemo(
    () =>
      applyIncreaseToRows(
        formState?.initiationRows ?? [],
        formState?.initiationIncrease ?? "",
      ),
    [formState?.initiationRows, formState?.initiationIncrease],
  );

  const calculatedLegalRows = useMemo(
    () =>
      applyIncreaseToRows(
        formState?.legalRows ?? [],
        formState?.legalIncrease ?? "",
      ),
    [formState?.legalRows, formState?.legalIncrease],
  );

  const calculatedAccountingRows = useMemo(() => {
    if (!formState) {
      return [];
    }
    if (syncAccountingWithLegal) {
      return calculatedLegalRows;
    }
    return applyIncreaseToRows(
      formState.accountingRows,
      formState.accountingIncrease,
    );
  }, [formState, syncAccountingWithLegal, calculatedLegalRows]);

  const calculatedInvestmentRows = useMemo(
    () =>
      applyIncreaseToRows(
        formState?.investmentRows ?? [],
        formState?.investmentIncrease ?? "",
      ),
    [formState?.investmentRows, formState?.investmentIncrease],
  );

  const handleSave = async () => {
    if (!formState || !selectedFiscalYear) {
      return;
    }

    const year = Number(selectedFiscalYear.id);
    const isDraft = isDraftFiscalYear(year, feesByYear, draftFiscalYears);
    const rowOptions = {
      syncAccountingWithLegal,
      initiationRows: calculatedInitiationRows,
      legalRows: calculatedLegalRows,
      accountingRows: calculatedAccountingRows,
      investmentRows: calculatedInvestmentRows,
    };

    const payload = isDraft
      ? buildCreateFiscalYearFeesPayload(
          year,
          feesByYear,
          formState,
          rowOptions,
        )
      : buildSaveFiscalYearFeesPayload(
          year,
          feesByYear,
          formState,
          rowOptions,
        );

    if (!payload) {
      setError(
        isDraft
          ? "Unable to save: missing prior fiscal year fee data."
          : "Unable to save: missing fiscal year fee record.",
      );
      return;
    }

    setSaving(true);
    setError("");
    try {
      await saveFiscalYearFees(payload);

      const docs = await getAllAccountingFees();
      const currentFiscalYear = getCurrentFiscalYear();
      const index = indexAccountingFeesByYear(docs);
      const options = buildFiscalYearOptions(docs, currentFiscalYear);
      const selectedOption =
        options.find((option) => Number(option.id) === year) ?? selectedFiscalYear;

      setFeesByYear(index);
      setFiscalYearOptions(options);
      setDraftFiscalYears((current) => current.filter((y) => y !== year));
      setDraftFormByYear((current) => {
        const next = { ...current };
        delete next[year];
        return next;
      });
      setSelectedFiscalYear(selectedOption);
      applyFormState(year, index);
      onClose();
    } catch {
      setError("Unable to save fiscal year fees. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const patchIncrease = (
    field:
      | "initiationIncrease"
      | "legalIncrease"
      | "accountingIncrease"
      | "investmentIncrease",
    value: string,
  ) => {
    setFormState((current) =>
      current ? { ...current, [field]: value } : current,
    );
  };

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={onClose}
      title="Manage Fiscal Year"
      titleColor="text-primary font-decimal text-2xl font-medium leading-8"
      compact
      compactBodyScrolls
      className="max-w-4xl"
    >
      <div className="flex flex-col gap-8">
        {loading ? (
          <p className="py-8 text-center text-gray-font">
            Loading fiscal year fees...
          </p>
        ) : (
          <>
            <section className={ACCOUNTING_PANEL_CLASS}>
              <h3 className={`${SECTION_TITLE_CLASS} mb-3`}>1. Fiscal Year</h3>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="w-full sm:max-w-xs">
                  <DropDownMultiple<DropDownOptionBase>
                    title="Fiscal Year"
                    options={fiscalYearOptions}
                    value={selectedFiscalYear}
                    searchable={false}
                    disabled={fiscalYearOptions.length === 0}
                    onChange={handleFiscalYearChange}
                  />
                </div>
                <Button
                  text="+ Create new fiscal year"
                  color="secondary"
                  variant="outlined"
                  size="fixed"
                  disabled={loading || fiscalYearOptions.length === 0}
                  onClick={handleCreateNewFiscalYear}
                />
              </div>
              <p className="mt-2 text-xs text-gray-font">
                Creation of past fiscal years is not allowed.
              </p>
            </section>

            {formState && (
              <>
                <section>
                  <h3 className={SECTION_TITLE_CLASS}>
                    2. Tariffs by Professionals Range
                  </h3>
                  <AccountingModalTabs
                    tabs={TARIFF_TABS}
                    active={activeTariffTab}
                    onChange={setActiveTariffTab}
                  />
                  <div className="mt-3 rounded-b-md border border-t-0 border-gray-200 bg-white p-4">
                    {activeTariffTab === "initiation" && (
                      <div className="flex flex-col gap-4">
                        <IncreaseField
                          prevYearRef={formState.prevYearInitiationRef}
                          value={formState.initiationIncrease}
                          onChange={(value) => patchIncrease("initiationIncrease", value)}
                        />
                        <TariffTable rows={calculatedInitiationRows} />
                      </div>
                    )}

                    {activeTariffTab === "legal" && (
                      <div className="flex flex-col gap-4">
                        <IncreaseField
                          prevYearRef={formState.prevYearLegalRef}
                          value={formState.legalIncrease}
                          onChange={(value) => patchIncrease("legalIncrease", value)}
                        />
                        <TariffTable rows={calculatedLegalRows} />
                      </div>
                    )}

                    {activeTariffTab === "accounting" && (
                      <div className="flex flex-col gap-4">
                        <div className={`${ACCOUNTING_INSET_PANEL_CLASS} mb-2`}>
                          <AccountingModalCheckbox
                            checked={syncAccountingWithLegal}
                            onChange={setSyncAccountingWithLegal}
                            label='Use the same values as the "Legal" tab'
                          />
                          <p className="ml-7 mt-1 text-xs text-gray-font">
                            When checked, calculations and ranges will
                            automatically reflect those set in Legal.
                          </p>
                        </div>
                        <div
                          className={
                            syncAccountingWithLegal
                              ? "pointer-events-none opacity-50 transition-opacity"
                              : "transition-opacity"
                          }
                        >
                          <IncreaseField
                            prevYearRef={formState.prevYearAccountingRef}
                            value={
                              syncAccountingWithLegal
                                ? formState.legalIncrease
                                : formState.accountingIncrease
                            }
                            onChange={(value) =>
                              patchIncrease(
                                syncAccountingWithLegal
                                  ? "legalIncrease"
                                  : "accountingIncrease",
                                value,
                              )
                            }
                            disabled={syncAccountingWithLegal}
                          />
                          <div className="mt-4">
                            <TariffTable rows={calculatedAccountingRows} />
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTariffTab === "investment" && (
                      <div className="flex flex-col gap-4">
                        <IncreaseField
                          prevYearRef={formState.prevYearInvestmentRef}
                          value={formState.investmentIncrease}
                          onChange={(value) => patchIncrease("investmentIncrease", value)}
                        />
                        <TariffTable
                          rows={calculatedInvestmentRows}
                          showRangeColumn={formState.investmentShowRangeColumn}
                        />
                      </div>
                    )}
                  </div>
                </section>

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <section className={ACCOUNTING_PANEL_CLASS}>
                    <h3
                      className={`${SECTION_TITLE_CLASS} border-b border-gray-200 pb-2`}
                    >
                      3. WSG Fund Percentage
                    </h3>
                    <div className="mt-4 flex flex-col gap-4">
                      <Input
                        title="Customizable Name"
                        value={formState.specialFundName}
                        error={false}
                        onChange={(e) =>
                          setFormState((current) =>
                            current
                              ? {
                                  ...current,
                                  specialFundName: e.target.value,
                                }
                              : current,
                          )
                        }
                      />
                      <div className="w-full max-w-[8rem]">
                        <Input
                          title="Percentage"
                          type="number"
                          value={formState.specialFundPercentage}
                          percentage
                          error={false}
                          onChange={(e) =>
                            setFormState((current) =>
                              current
                                ? {
                                    ...current,
                                    specialFundPercentage: e.target.value,
                                  }
                                : current,
                            )
                          }
                        />
                      </div>
                    </div>
                  </section>

                  <section className={ACCOUNTING_PANEL_CLASS}>
                    <h3
                      className={`${SECTION_TITLE_CLASS} border-b border-gray-200 pb-2`}
                    >
                      4. Annual Meeting Fee{" "}
                      <span className="text-xs font-normal text-gray-font">
                        (For Accounting)
                      </span>
                    </h3>
                    <div className="mt-4">
                      <div className="flex flex-col gap-2">
                        <span className="text-sm font-medium text-gray-font">
                          Annual Meeting Reg. Fee
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-font">$</span>
                          <Input
                            title=""
                            type="number"
                            value={formState.annualMeetingFee}
                            error={false}
                            onChange={(e) =>
                              setFormState((current) =>
                                current
                                  ? {
                                      ...current,
                                      annualMeetingFee: e.target.value,
                                    }
                                  : current,
                              )
                            }
                          />
                        </div>
                        <p className="text-xs text-gray-font">
                          Updated for billing pop-up (Varies by year. Base
                          2025-2026 = 1000 USD).
                        </p>
                      </div>
                    </div>
                  </section>
                </div>
              </>
            )}

            <AccountingModalError message={error} />
          </>
        )}

        <AccountingModalFooter
          onCancel={onClose}
          onSubmit={() => void handleSave()}
          submitText="Save Changes"
          saving={saving}
          submitDisabled={loading || saving || !formState}
        />
      </div>
    </Modal>
  );
}
